import React, { useEffect, useState } from 'react';
import { consumptionAPI, exportAPI, downloadAuthFile } from '@/lib/api';
import { getPeriodOptions } from '@/lib/utils';
import { toast } from 'sonner';
import {
    BarChart3,
    Download,
    TrendingDown,
    TrendingUp,
    Minus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function ConsumptionTab({ siteId, siteName, siteIds, period }) {
    const [consumptionData, setConsumptionData] = useState([]);
    const [loading, setLoading] = useState(false);
    // Multi-month range mode (local to this tab; parent period is reused as default for both ends)
    const [rangeMode, setRangeMode] = useState(false);
    const [startPeriod, setStartPeriod] = useState(period);
    const [endPeriod, setEndPeriod] = useState(period);
    const periodOptions = getPeriodOptions();

    // Aggregation mode: multiple site_ids OR a single site_id
    const isMulti = Array.isArray(siteIds) && siteIds.length > 0;
    const hasSelection = isMulti || !!siteId;

    useEffect(() => {
        if (!rangeMode) {
            setStartPeriod(period);
            setEndPeriod(period);
        }
    }, [period, rangeMode]);

    useEffect(() => {
        if (hasSelection) {
            loadConsumption();
        } else {
            setConsumptionData([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteId, siteIds && siteIds.join(','), period, rangeMode, startPeriod, endPeriod]);

    const loadConsumption = async () => {
        setLoading(true);
        try {
            let response;
            if (rangeMode) {
                const [s, e] = startPeriod <= endPeriod ? [startPeriod, endPeriod] : [endPeriod, startPeriod];
                response = isMulti
                    ? await consumptionAPI.getRangeMulti(siteIds, s, e)
                    : await consumptionAPI.getRange(siteId, s, e);
            } else {
                response = isMulti
                    ? await consumptionAPI.getMulti(siteIds, period)
                    : await consumptionAPI.get(siteId, period);
            }
            setConsumptionData(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des donnees de consommation');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!hasSelection) return;
        // Excel export not yet multi-site; pick the first site as a fallback.
        const exportSiteId = isMulti ? siteIds[0] : siteId;
        const p = rangeMode ? endPeriod : period;
        downloadAuthFile(exportAPI.consumption(exportSiteId, p), 'consommation.xlsx');
    };

    const productsReceived = consumptionData.filter(item => item.receptions > 0).length;
    const productsConsumed = consumptionData.filter(item => item.consumption !== 0).length;

    if (!hasSelection) {
        return (
            <Card>
                <CardContent className="py-12 text-center">
                    <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-slate-500">Selectionnez un site specifique pour afficher la consommation</p>
                </CardContent>
            </Card>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4" data-testid="consumption-tab-content">
            {/* Period selector + Export */}
            <Card>
                <CardContent className="p-3 flex flex-wrap items-end gap-3">
                    <div className="space-y-1 flex-1 min-w-[260px]">
                        <div className="flex items-center justify-between">
                            <Label className="text-xs">{rangeMode ? 'Plage de mois' : 'Période analysée'}</Label>
                            <button
                                type="button"
                                onClick={() => setRangeMode(!rangeMode)}
                                className="text-xs text-blue-600 hover:underline font-semibold"
                                data-testid="consumption-range-toggle"
                            >
                                {rangeMode ? '← Mois unique' : 'Sur plusieurs mois →'}
                            </button>
                        </div>
                        {rangeMode ? (
                            <div className="flex items-center gap-2">
                                <Select value={startPeriod} onValueChange={setStartPeriod}>
                                    <SelectTrigger className="h-9 flex-1" data-testid="consumption-start-period-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {periodOptions.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <span className="text-slate-400 text-sm">→</span>
                                <Select value={endPeriod} onValueChange={setEndPeriod}>
                                    <SelectTrigger className="h-9 flex-1" data-testid="consumption-end-period-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {periodOptions.map(opt => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        ) : (
                            <div className="h-9 px-3 flex items-center rounded-md border bg-slate-50 text-sm text-slate-600">
                                {period} <span className="text-slate-400 ml-2 text-xs">(défini en haut de la page)</span>
                            </div>
                        )}
                        {rangeMode && (
                            <div className="text-[11px] text-slate-500 bg-blue-50 border border-blue-100 rounded-md px-2.5 py-1.5 mt-1">
                                Stock initial = inventaire <strong>{startPeriod}</strong> ·
                                Stock final = inventaire <strong>{endPeriod}</strong> ·
                                Réceptions cumulées sur la plage
                            </div>
                        )}
                    </div>
                    <Button variant="outline" size="sm" onClick={handleExport} data-testid="export-consumption-btn">
                        <Download className="w-4 h-4 mr-2" />
                        Export Excel
                    </Button>
                </CardContent>
            </Card>

            {/* (Removed) standalone Export button — now merged in the period card */}

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-blue-500" />
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Produits receptionnes</p>
                                <p className="text-3xl font-bold text-slate-900">{productsReceived}</p>
                            </div>
                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                                <TrendingUp className="w-6 h-6 text-blue-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-orange-500" />
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Produits consommes</p>
                                <p className="text-3xl font-bold text-slate-900">{productsConsumed}</p>
                            </div>
                            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                                <TrendingDown className="w-6 h-6 text-orange-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <Card className="relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500" />
                    <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-500">Produits suivis</p>
                                <p className="text-3xl font-bold text-slate-900">{consumptionData.length}</p>
                            </div>
                            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                                <BarChart3 className="w-6 h-6 text-emerald-600" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Detailed Table */}
            <Card>
                <CardHeader>
                    <CardTitle>Detail par produit - {siteName}{isMulti && siteIds.length > 1 ? ` (${siteIds.length} sites cumulés)` : ''}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {consumptionData.length === 0 ? (
                        <div className="py-12 text-center">
                            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                            <p className="text-slate-500">Aucune donnee pour cette periode</p>
                            <p className="text-sm text-slate-400 mt-2">
                                Saisissez des inventaires et des receptions pour voir les calculs de consommation
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full data-table">
                                <thead>
                                    <tr className="table-header">
                                        <th className="text-left p-4">Produit</th>
                                        <th className="text-left p-4">Fournisseur</th>
                                        <th className="text-right p-4">Stock Initial (kg)</th>
                                        <th className="text-right p-4">Receptions</th>
                                        <th className="text-center p-4">Unite</th>
                                        <th className="text-right p-4">Recept. (kg)</th>
                                        <th className="text-right p-4">Stock Final</th>
                                        <th className="text-right p-4">Consommation</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(() => {
                                        let currentCategory = null;

                                        const productGroups = {};
                                        consumptionData.forEach(item => {
                                            if (!productGroups[item.product_id]) productGroups[item.product_id] = [];
                                            productGroups[item.product_id].push(item);
                                        });

                                        const rendered = [];
                                        const processedProducts = new Set();

                                        consumptionData.forEach((item, index) => {
                                            const showCategoryHeader = item.category !== currentCategory;
                                            currentCategory = item.category;

                                            if (showCategoryHeader) {
                                                rendered.push(
                                                    <tr key={`cat-${item.category}`} className="bg-slate-100">
                                                        <td colSpan="8" className="p-3 font-semibold text-slate-700">
                                                            {item.category || 'Sans categorie'}
                                                        </td>
                                                    </tr>
                                                );
                                            }

                                            const group = productGroups[item.product_id];
                                            const isMulti = group.length > 1;

                                            if (isMulti && !processedProducts.has(item.product_id)) {
                                                processedProducts.add(item.product_id);
                                                const totalInitial = group.reduce((s, i) => s + (i.initial_stock || 0), 0);
                                                const totalRecCap = group.reduce((s, i) => s + (i.receptions_capacity || 0), 0);
                                                const totalFinal = group.reduce((s, i) => s + (i.final_stock || 0), 0);
                                                const firstItem = group.find(i => i.is_first_for_product) || group[0];
                                                const consumption = firstItem.consumption;
                                                const capUnit = firstItem.capacity_unit;

                                                rendered.push(
                                                    <tr key={`total-${item.product_id}`} className="hover:bg-slate-50/50" style={{ lineHeight: '1.2' }}>
                                                        <td className="pt-0.5 pb-0 px-4 pl-8">
                                                            <div className="font-semibold text-sm text-slate-700 leading-tight">{item.product_name}</div>
                                                        </td>
                                                        <td className="py-0 px-4 text-slate-500 text-sm">-</td>
                                                        <td className="py-0 px-4 text-right text-sm font-semibold text-slate-700">
                                                            {totalInitial > 0 ? `${totalInitial.toFixed(2)} ${capUnit}` : '-'}
                                                        </td>
                                                        <td className="py-0 px-4 text-right">-</td>
                                                        <td className="py-0 px-4 text-center">-</td>
                                                        <td className="py-0 px-4 text-right text-emerald-600 font-semibold text-sm">
                                                            {totalRecCap > 0 ? `${totalRecCap.toFixed(2)} ${capUnit}` : '-'}
                                                        </td>
                                                        <td className="py-0 px-4 text-right font-semibold text-slate-700">
                                                            {totalFinal > 0 ? `${totalFinal.toFixed(2)}` : '-'}
                                                        </td>
                                                        <td className="py-0 px-4 text-right">
                                                            {consumption !== 0 ? (
                                                                <span className={`inline-flex items-center gap-1 font-semibold ${
                                                                    consumption > 0 ? 'text-orange-600' :
                                                                    consumption < 0 ? 'text-emerald-600' : 'text-slate-400'
                                                                }`}>
                                                                    {consumption > 0 ? (
                                                                        <TrendingDown className="w-4 h-4" />
                                                                    ) : consumption < 0 ? (
                                                                        <TrendingUp className="w-4 h-4" />
                                                                    ) : (
                                                                        <Minus className="w-4 h-4" />
                                                                    )}
                                                                    {consumption.toFixed(2)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );

                                                group.forEach((subItem) => {
                                                    const subKey = subItem.supplier_product_id || `${subItem.product_id}-sub`;
                                                    rendered.push(
                                                        <tr key={subKey} style={{ lineHeight: '1.2' }}>
                                                            <td className="py-0 px-4 pl-8">
                                                                <div className="text-sm text-slate-400 pl-4 leading-tight">
                                                                    {subItem.supplier_product_name || '-'}
                                                                </div>
                                                            </td>
                                                            <td className="py-0 px-4 text-slate-500 text-sm">{subItem.supplier_name || '-'}</td>
                                                            <td className="py-0 px-4 text-right text-sm">
                                                                {subItem.initial_stock > 0 ? `${subItem.initial_stock.toFixed(2)} ${subItem.capacity_unit}` : '-'}
                                                            </td>
                                                            <td className="py-0 pl-4 pr-1 text-right text-emerald-600 font-medium">
                                                                +{subItem.receptions.toFixed(2)}
                                                            </td>
                                                            <td className="py-0 pl-0 pr-4 text-left">
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs font-medium">
                                                                    {subItem.unit}
                                                                </span>
                                                            </td>
                                                            <td className="py-0 px-4 text-right text-emerald-500 text-sm">
                                                                {subItem.receptions_capacity?.toFixed(2) || '0.00'} {subItem.capacity_unit}
                                                            </td>
                                                            <td className="py-0 px-4 text-right">-</td>
                                                            <td className="py-0 px-4 text-right"><span className="text-slate-400">-</span></td>
                                                        </tr>
                                                    );
                                                });
                                            } else if (!isMulti) {
                                                const uniqueKey = item.supplier_product_id || `${item.product_id}-${index}`;
                                                rendered.push(
                                                    <tr key={uniqueKey} className="border-b border-slate-100 hover:bg-slate-50/50">
                                                        <td className="pt-1 pb-1 px-4 pl-8">
                                                            <div className="font-semibold text-sm text-slate-700 mb-0">{item.product_name}</div>
                                                            {item.supplier_product_name && (
                                                                <div className="text-sm text-slate-400 pl-4 leading-tight">
                                                                    {item.supplier_product_name}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="pt-2 pb-0.5 px-4 align-bottom text-slate-500 text-sm">{item.supplier_name || '-'}</td>
                                                        <td className="pt-2 pb-0.5 px-4 align-bottom text-right text-sm">
                                                            {item.initial_stock > 0 ? `${item.initial_stock.toFixed(2)} ${item.capacity_unit}` : '-'}
                                                        </td>
                                                        <td className="pt-2 pb-0.5 pl-4 pr-1 align-bottom text-right text-emerald-600 font-medium">
                                                            +{item.receptions.toFixed(2)}
                                                        </td>
                                                        <td className="pt-2 pb-0.5 pl-0 pr-4 align-bottom text-left">
                                                            <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs font-medium">
                                                                {item.unit}
                                                            </span>
                                                        </td>
                                                        <td className="pt-2 pb-0.5 px-4 align-bottom text-right text-emerald-500 text-sm">
                                                            {item.receptions_capacity?.toFixed(2) || '0.00'} {item.capacity_unit}
                                                        </td>
                                                        <td className="pt-2 pb-0.5 px-4 align-bottom text-right">
                                                            {item.final_stock > 0 ? item.final_stock.toFixed(2) : '-'}
                                                        </td>
                                                        <td className="pt-2 pb-0.5 px-4 align-bottom text-right">
                                                            {item.consumption !== 0 ? (
                                                                <span className={`inline-flex items-center gap-1 font-semibold ${
                                                                    item.consumption > 0 ? 'text-orange-600' :
                                                                    item.consumption < 0 ? 'text-emerald-600' : 'text-slate-400'
                                                                }`}>
                                                                    {item.consumption > 0 ? (
                                                                        <TrendingDown className="w-4 h-4" />
                                                                    ) : item.consumption < 0 ? (
                                                                        <TrendingUp className="w-4 h-4" />
                                                                    ) : (
                                                                        <Minus className="w-4 h-4" />
                                                                    )}
                                                                    {item.consumption.toFixed(2)}
                                                                </span>
                                                            ) : (
                                                                <span className="text-slate-400">-</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                        });

                                        return rendered;
                                    })()}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Formula explanation */}
            <Card>
                <CardContent className="p-6">
                    <h4 className="font-semibold text-slate-900 mb-2">Formule de calcul</h4>
                    <p className="text-slate-600">
                        <span className="font-mono bg-slate-100 px-2 py-1 rounded">
                            Consommation = Stock Initial + Receptions - Stock Final
                        </span>
                    </p>
                    <p className="text-sm text-slate-500 mt-2">
                        Une consommation positive indique des produits utilises. Une valeur negative peut indiquer une erreur de saisie.
                    </p>
                </CardContent>
            </Card>
        </div>
    );
}
